/**
 * Packeta Widget Service
 *
 * Integration with Packeta's PUDO (Pick-Up/Drop-Off) delivery widget.
 * Allows users to select pickup points for package delivery.
 *
 * @see https://docs.packeta.com/docs/pudo-delivery/widget
 */
import { Effect, Exit, Layer, Schema } from 'effect';

import type { PacketaKey } from '../../config/packeta';
import { createScript, loadOf } from '../shipping/domAsset';
import type { PacketaWindow } from './window';
import {
  PacketaSelectionSchema,
  PacketaWidget,
  PacketaWidgetCancelledError,
  PacketaWidgetLayout,
  PacketaWidgetNotLoadedError,
  PacketaWidgetPayloadError,
  PacketaWidgetScriptLoadError,
  toPacketaT,
} from './PacketaWidget';
import type { PacketaT, PacketaWidgetOptions } from './PacketaWidget';

const decodeSelection = Schema.decodeUnknownExit(PacketaSelectionSchema);

const WIDGET_SCRIPT_URL = 'https://widget.packeta.com/v6/www/js/library.js';

const packetaWindow = (): PacketaWindow | undefined =>
  typeof window === 'undefined' ? undefined : window;

const loadWidgetScript = Effect.tryPromise({
  try: async () => {
    const target = packetaWindow();
    if (target === undefined) {
      throw new Error('Window is not available (SSR context)');
    }

    if (target.Packeta?.Widget?.pick) {
      return;
    }

    const existing = document.querySelector(`script[src="${WIDGET_SCRIPT_URL}"]`);
    if (existing !== null) {
      return loadOf(existing, 'the Packeta widget script');
    }

    const script = createScript(WIDGET_SCRIPT_URL);
    const loaded = loadOf(script, 'the Packeta widget script');
    document.body.appendChild(script);
    return loaded;
  },
  catch: (error) =>
    new PacketaWidgetScriptLoadError({
      message: 'Failed to load Packeta widget script',
      cause: error,
    }),
});

const closeWidget = Effect.sync(() => {
  packetaWindow()?.Packeta?.Widget?.close?.();
});

type PacketaOpenError =
  | PacketaWidgetNotLoadedError
  | PacketaWidgetCancelledError
  | PacketaWidgetPayloadError;

const openWidget = (
  apiKey: PacketaKey,
  options: PacketaWidgetOptions
): Effect.Effect<PacketaT, PacketaOpenError> =>
  Effect.callback<PacketaT, PacketaOpenError>((resume) => {
    const target = packetaWindow();

    if (target === undefined) {
      resume(
        Effect.fail(
          new PacketaWidgetNotLoadedError({
            message: 'Window is not available (SSR context)',
          })
        )
      );
      return;
    }

    if (!target.Packeta?.Widget?.pick) {
      resume(
        Effect.fail(
          new PacketaWidgetNotLoadedError({
            message: 'Packeta widget script is not loaded',
          })
        )
      );
      return;
    }

    // The widget documents lower-case ISO country codes; the storefront's
    // `CountryT` is upper-case.
    const config: Record<string, unknown> = {
      language: options.language,
      country: options.country.toLowerCase(),
      layout: options.layout ?? PacketaWidgetLayout.PUDO,
    };

    const inlineContainer = options.inlineContainerId
      ? (document.getElementById(options.inlineContainerId) ?? undefined)
      : undefined;

    try {
      target.Packeta.Widget.pick(
        apiKey,
        (point) => {
          // `null` is the widget's documented cancel signal. A non-null point
          // is untrusted third-party data and goes through the schema; one
          // this client cannot read is a payload error, not a silent miss.
          if (point === null || point === undefined) {
            resume(
              Effect.fail(
                new PacketaWidgetCancelledError({
                  message: 'User cancelled Packeta widget selection',
                })
              )
            );
            return;
          }

          resume(
            Exit.match(decodeSelection(point), {
              onFailure: (cause) =>
                Effect.fail(
                  new PacketaWidgetPayloadError({
                    message: 'The Packeta widget sent a point this client cannot read',
                    cause,
                  })
                ),
              onSuccess: (selection) => Effect.succeed(toPacketaT(selection)),
            })
          );
        },
        config,
        inlineContainer
      );
    } catch {
      resume(
        Effect.fail(
          new PacketaWidgetNotLoadedError({
            message: 'Failed to initialize Packeta widget',
          })
        )
      );
    }

    return closeWidget;
  });

const make = Effect.succeed({
  loadScript: loadWidgetScript,

  pick: (apiKey: PacketaKey, options: PacketaWidgetOptions) =>
    Effect.gen(function* () {
      yield* loadWidgetScript;
      return yield* openWidget(apiKey, options);
    }),

  close: closeWidget,
});

export const PacketaWidgetLive = Layer.effect(PacketaWidget, make);
